import { useState, useEffect, useRef } from "react";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { HomeButton } from "../components/HomeButton.jsx";

const PRESETS = [
  { label: "Classic", work: 25, short: 5, long: 15, rounds: 4 },
  { label: "Deep Focus", work: 50, short: 10, long: 20, rounds: 3 },
  { label: "Quick Bursts", work: 15, short: 3, long: 10, rounds: 6 },
];

const SOUNDS = [
  { id: "none", label: "None", emoji: "🔇" },
  { id: "white", label: "White", emoji: "⚪" },
  { id: "pink", label: "Pink", emoji: "pink" },
  { id: "brown", label: "Brown", emoji: "🟤" },
];

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const ROSE = "#F43F5E";
const PINK = "#EC4899";
const ROSE_LIGHT = "#FDA4AF";

/**
 * Ambient noise generator.
 *
 * V60.10 revert — back to the V57-era Web Audio API implementation.
 *
 * Why the revert: V60.8 rewrote this hook from Web Audio to HTML
 * <audio> + MediaSession, aiming to keep noise playing when the iPhone
 * PWA locks. That worked for "noise continues when locked" but
 * introduced an audible gap at every loop boundary (every 60-90s)
 * that we couldn't eliminate — iOS's HTML audio loop scheduler stops
 * and restarts the element rather than looping it seamlessly.
 *
 * The real use case clarification: StudyTimer is a Pomodoro focus
 * timer, not a sleep app. Focus users run it with the screen on, so
 * Web Audio suspending on lock isn't an issue for the design use case.
 * The sleep use case (Ian's wife) is better served by opening
 * NerdSuite in Safari (not the PWA) where Web Audio plays seamlessly
 * without locking issues. StudyTimer was never designed for sleep —
 * users who want it for that can use the Safari path.
 *
 * Web Audio in browser Safari (where the sleep use case lives) loops
 * seamlessly with zero gap. That's the better experience for the
 * design use case AND the sleep workaround use case.
 *
 * iOS Safari / PWA notes (original V57 context, still applicable):
 *  - iOS caps the number of AudioContexts you can create (historically ~4).
 *    The old code did `new AudioContext()` + `ctx.close()` on EVERY sound or
 *    timer toggle. After a few toggles iOS refused to make more contexts and
 *    everything went silent with no error. Desktop has no cap, so it tested
 *    fine. Fix: ONE persistent context for the component's life.
 *  - iOS starts an AudioContext "suspended" unless resumed inside a real user
 *    gesture. Fix: resume() is called from the actual tap handler (see the
 *    sound buttons), and again here defensively.
 *  - This routes through the ringer channel on iOS, so the hardware mute
 *    switch silences it with no error — there's a UI note for that.
 *
 * Returns nothing; driven entirely by the soundId arg. Decoupled from the
 * timer so ambient sound works whether or not a Pomodoro is running.
 */
function useAmbientSound(soundId) {
  const ctxRef = useRef(null);
  const srcRef = useRef(null);
  const gainRef = useRef(null);

  // Lazily build (or reuse) the single AudioContext, then start/swap the
  // noise source. Never closes the context — only stops the source node.
  useEffect(() => {
    // Stop whatever is currently playing.
    if (srcRef.current) {
      try { srcRef.current.stop(); } catch (e) {}
      try { srcRef.current.disconnect(); } catch (e) {}
      srcRef.current = null;
    }

    if (soundId === "none") return;

    try {
      // Reuse the existing context if we already made one. This is the
      // core regression fix: never `new AudioContext()` per toggle.
      let ctx = ctxRef.current;
      if (!ctx) {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        ctxRef.current = ctx;
        const g = ctx.createGain();
        g.gain.value = 1.0;
        g.connect(ctx.destination);
        gainRef.current = g;
      }
      // Defensive resume (the tap handler also resumes; belt and braces).
      if (ctx.state === "suspended") ctx.resume();

      const bufferSize = 2 * ctx.sampleRate;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      const gain = gainRef.current;

      if (soundId === "white") {
        // White noise: equal energy across all frequencies.
        for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
        gain.gain.value = 0.06;
      } else if (soundId === "pink") {
        // Pink noise: -3dB/octave rolloff (1/f noise).
        let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          b0 = 0.99886 * b0 + white * 0.0555179;
          b1 = 0.99332 * b1 + white * 0.0750759;
          b2 = 0.96900 * b2 + white * 0.1538520;
          b3 = 0.86650 * b3 + white * 0.3104856;
          b4 = 0.55000 * b4 + white * 0.5329522;
          b5 = -0.7616 * b5 - white * 0.0168980;
          data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
          b6 = white * 0.115926;
        }
        gain.gain.value = 0.09;
      } else if (soundId === "brown") {
        // Brown noise: -6dB/octave rolloff (random walk).
        let last = 0;
        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          last = (last + (0.02 * white)) / 1.02;
          data[i] = last * 3.5;
        }
        gain.gain.value = 0.15;
      }

      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(gain);
      src.start();
      srcRef.current = src;
    } catch (e) {}
  }, [soundId]);

  // Stop sound on unmount, but keep the context alive (don't close()).
  useEffect(() => {
    return () => {
      if (srcRef.current) {
        try { srcRef.current.stop(); } catch (e) {}
        try { srcRef.current.disconnect(); } catch (e) {}
        srcRef.current = null;
      }
    };
  }, []);

  // Exposed so a real user-gesture handler can resume a suspended context
  // (iOS requires the resume to happen inside the tap, not in an effect).
  return () => {
    const ctx = ctxRef.current;
    if (ctx && ctx.state === "suspended") ctx.resume();
  };
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2,"0")}:${s.toString().padStart(2,"0")}`;
}

export default function PomodoroApp() {
  const [preset, setPreset] = useState(PRESETS[0]);
  const [phase, setPhase] = useState("work"); // work, short, long
  const [timeLeft, setTimeLeft] = useState(PRESETS[0].work * 60);
  const [running, setRunning] = useState(false);
  const [round, setRound] = useState(1);
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [sound, setSound] = useState("none");
  const [showSettings, setShowSettings] = useState(false);
  const [customWork, setCustomWork] = useState("");
  const [customShort, setCustomShort] = useState("");
  const [customLong, setCustomLong] = useState("");
  const intervalRef = useRef(null);

  // Ambient sound is decoupled from the timer — it plays whenever a sound
  // is selected, running or not. resumeAudio() must be called from a real
  // tap (iOS won't resume a suspended context from inside an effect).
  const resumeAudio = useAmbientSound(sound);

  // Timer logic
  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            // Phase complete
            if (phase === "work") {
              setSessionsCompleted(s => s + 1);
              setTotalMinutes(m => m + preset.work);
              if (round >= preset.rounds) {
                setPhase("long");
                setRound(1);
                return preset.long * 60;
              } else {
                setPhase("short");
                return preset.short * 60;
              }
            } else {
              if (phase === "short") setRound(r => r + 1);
              setPhase("work");
              return preset.work * 60;
            }
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, phase, round, preset]);

  const toggleRunning = () => setRunning(r => !r);

  const reset = () => {
    setRunning(false);
    setPhase("work");
    setTimeLeft(preset.work * 60);
    setRound(1);
  };

  const skip = () => {
    setRunning(false);
    if (phase === "work") {
      if (round >= preset.rounds) {
        setPhase("long");
        setTimeLeft(preset.long * 60);
        setRound(1);
      } else {
        setPhase("short");
        setTimeLeft(preset.short * 60);
      }
    } else {
      if (phase === "short") setRound(r => r + 1);
      setPhase("work");
      setTimeLeft(preset.work * 60);
    }
  };

  const applyPreset = (p) => {
    setPreset(p);
    setRunning(false);
    setPhase("work");
    setTimeLeft(p.work * 60);
    setRound(1);
  };

  const applyCustom = () => {
    const w = parseInt(customWork) || 25;
    const s = parseInt(customShort) || 5;
    const l = parseInt(customLong) || 15;
    const p = { label: "Custom", work: w, short: s, long: l, rounds: 4 };
    applyPreset(p);
    setShowSettings(false);
  };

  const totalDuration = phase === "work" ? preset.work * 60 : phase === "short" ? preset.short * 60 : preset.long * 60;
  const progress = 1 - (timeLeft / totalDuration);
  const circumference = 2 * Math.PI * 140;
  const strokeDashoffset = circumference * (1 - progress);

  const phaseLabel = phase === "work" ? "Focus Time" : phase === "short" ? "Short Break" : "Long Break";
  const phaseColor = phase === "work" ? ROSE : phase === "short" ? "#10B981" : "#3B82F6";

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(ROSE, 0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(ROSE, 0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor={ROSE} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${ROSE}, ${PINK})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>P</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${ROSE_LIGHT}, #F9A8D4)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>StudyTimer</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdStack</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>

        {/* Phase indicator */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span style={{
            display: "inline-block", padding: "6px 16px", borderRadius: 20,
            background: rgba(phaseColor, 0.1), border: `1px solid ${rgba(phaseColor, 0.2)}`,
            fontSize: 13, fontWeight: 600, color: phaseColor, letterSpacing: "0.5px",
          }}>{phaseLabel}</span>
        </div>

        {/* Timer circle */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "24px 0", position: "relative" }}>
          <svg width="320" height="320" viewBox="0 0 320 320" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="160" cy="160" r="140" fill="none" stroke={rgba(phaseColor, 0.08)} strokeWidth="8" />
            <circle cx="160" cy="160" r="140" fill="none" stroke={phaseColor} strokeWidth="8"
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
              style={{ transition: "stroke-dashoffset 0.5s linear, stroke 0.3s ease" }} />
          </svg>
          <div style={{ position: "absolute", textAlign: "center" }}>
            <div style={{ fontSize: 64, fontWeight: 800, letterSpacing: "-2px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>
              {formatTime(timeLeft)}
            </div>
            <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>
              Round {round} of {preset.rounds}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 32 }}>
          <button onClick={reset} style={{
            width: 48, height: 48, borderRadius: 14, border: `1px solid ${rgba(ROSE, 0.15)}`,
            background: rgba(ROSE, 0.05), color: ROSE_LIGHT, cursor: "pointer",
            fontSize: 18, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
          }}>↺</button>

          <button onClick={toggleRunning} style={{
            width: 140, height: 48, borderRadius: 14, border: "none",
            background: running ? rgba(ROSE, 0.15) : `linear-gradient(135deg, ${ROSE}, ${PINK})`,
            color: running ? ROSE_LIGHT : "#fff", cursor: "pointer",
            fontSize: 16, fontWeight: 700, fontFamily: "inherit",
            boxShadow: !running ? `0 0 30px ${rgba(ROSE, 0.25)}` : "none",
          }}>{running ? "Pause" : "Start"}</button>

          <button onClick={skip} style={{
            width: 48, height: 48, borderRadius: 14, border: `1px solid ${rgba(ROSE, 0.15)}`,
            background: rgba(ROSE, 0.05), color: ROSE_LIGHT, cursor: "pointer",
            fontSize: 18, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
          }}>⏭</button>
        </div>

        {/* Preset selector */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 24 }}>
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)} style={{
              padding: "12px 8px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
              background: preset.label === p.label ? rgba(ROSE, 0.1) : "rgba(255,255,255,0.02)",
              border: preset.label === p.label ? `1.5px solid ${rgba(ROSE, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: preset.label === p.label ? ROSE_LIGHT : "#6B7280" }}>{p.label}</span>
              <span style={{ fontSize: 10, color: preset.label === p.label ? ROSE : "#374151" }}>{p.work}/{p.short}/{p.long}</span>
            </button>
          ))}
          <button onClick={() => setShowSettings(!showSettings)} style={{
            padding: "12px 8px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
            background: showSettings ? rgba(ROSE, 0.1) : "rgba(255,255,255,0.02)",
            border: showSettings ? `1.5px solid ${rgba(ROSE, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: showSettings ? ROSE_LIGHT : "#6B7280" }}>Custom</span>
            <span style={{ fontSize: 10, color: "#374151" }}>Set times</span>
          </button>
        </div>

        {/* Custom settings */}
        {showSettings && (
          <div style={{
            padding: "20px", borderRadius: 16, marginBottom: 24,
            background: rgba(ROSE, 0.03), border: `1px solid ${rgba(ROSE, 0.08)}`,
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[
                { label: "Work (min)", val: customWork, set: setCustomWork, ph: "25" },
                { label: "Short Break", val: customShort, set: setCustomShort, ph: "5" },
                { label: "Long Break", val: customLong, set: setCustomLong, ph: "15" },
              ].map(f => (
                <div key={f.label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.label}</label>
                  <input type="number" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                    style={{
                      width: "100%", padding: "10px 12px", borderRadius: 10,
                      border: `1px solid ${rgba(ROSE, 0.12)}`, background: "rgba(0,0,0,0.3)",
                      color: "#E2E8F0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                    }} />
                </div>
              ))}
            </div>
            <button onClick={applyCustom} style={{
              width: "100%", padding: "12px", borderRadius: 12, border: "none", cursor: "pointer",
              background: `linear-gradient(135deg, ${ROSE}, ${PINK})`, color: "#fff",
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            }}>Apply Custom Timer</button>
          </div>
        )}

        {/* Ambient sounds */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10, textAlign: "center" }}>Ambient Sound</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            {SOUNDS.map(s => (
              <button key={s.id} onClick={() => { resumeAudio(); setSound(s.id); }} style={{
                padding: "10px 14px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
                background: sound === s.id ? rgba(ROSE, 0.1) : "rgba(255,255,255,0.02)",
                border: sound === s.id ? `1.5px solid ${rgba(ROSE, 0.3)}` : "1.5px solid rgba(255,255,255,0.05)",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>{s.emoji === "pink" ? <span style={{ display: "inline-block", width: 18, height: 18, borderRadius: "50%", background: "#F472B6", verticalAlign: "middle" }}></span> : s.emoji}</span>
                <span style={{ fontSize: 10, color: sound === s.id ? ROSE_LIGHT : "#6B7280" }}>{s.label}</span>
              </button>
            ))}
          </div>
          {sound !== "none" && (
            <div style={{ fontSize: 10, color: "#374151", textAlign: "center", marginTop: 8 }}>
              No sound? Check your phone's mute switch.
            </div>
          )}
        </div>

        {/* Session stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <div style={{
            padding: "18px 14px", borderRadius: 14, textAlign: "center",
            background: rgba(ROSE, 0.04), border: `1px solid ${rgba(ROSE, 0.1)}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Sessions</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: ROSE_LIGHT }}>{sessionsCompleted}</div>
          </div>
          <div style={{
            padding: "18px 14px", borderRadius: 14, textAlign: "center",
            background: rgba(ROSE, 0.04), border: `1px solid ${rgba(ROSE, 0.1)}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Focus Time</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: ROSE_LIGHT }}>{totalMinutes}<span style={{ fontSize: 14, color: "#6B7280" }}>m</span></div>
          </div>
          <div style={{
            padding: "18px 14px", borderRadius: 14, textAlign: "center",
            background: rgba(ROSE, 0.04), border: `1px solid ${rgba(ROSE, 0.1)}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Current Streak</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: ROSE_LIGHT }}>{sessionsCompleted > 0 ? "1" : "0"}<span style={{ fontSize: 14, color: "#6B7280" }}>d</span></div>
          </div>
        </div>

        {/* Cross-promo */}
        <div style={{ marginTop: 40, padding: "20px", borderRadius: 16, background: "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(59,130,246,0.06))", border: "1px solid rgba(139,92,246,0.12)", textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#A5B4FC", marginBottom: 6 }}>Study smarter between sessions</div>
          {/* Cross-promo (rotates per page load) */}
          <CrossPromo currentSlug="timer" accent={ROSE} accent2={PINK} accentLight={ROSE_LIGHT} />
        </div>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(ROSE, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
        input::placeholder { color: #374151; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
